// `?meshview` — render an OGS character's GEOMETRY instead of its Gaussians.
//
// The splat render is the decoder's answer, and the decoder answers even when
// the rig is doing nothing: every visible change could be `geom`'s offsets
// responding to the control vector rather than the mesh moving. That ambiguity
// is exactly what makes "the face is static" hard to diagnose — and no existing
// switch resolves it, because they all still draw Gaussians (whose scales and
// opacities are decoder output too) or change the rig rather than what is drawn
// from it.
//
// So this draws the thing itself: one THREE mesh per branch, built from that
// branch's faces and posed vertices — the SAME `verts` the lift anchors to. If
// the head moves here, the rig is driving the mesh; if it does not, no amount of
// decoder tuning will make the geometry animate.
//
// Placement mirrors SplatMeshUpdater exactly (scale → rotation → offset), so the
// mesh lands where the splats would and the two can be flipped between (or
// overlaid, with `?meshview=both`) without anything shifting.
//
// The vertices only reach the CPU on the CPU deform path, so the flag forces it
// (ogsGpuFlags: `gpuDeform && !meshView`). That is a real per-frame cost and is
// why this is opt-in and reported, never a default.

import * as THREE from 'three'
import { applyMeshPlacement } from './SplatMeshUpdater.ts'

/** Per-branch wireframe colour, so head/body/clothes/hair stay tellable apart. */
const BRANCH_COLORS = {
  head: 0x7fd4ff,
  body: 0xffd27f,
  teeth: 0xffffff,
  clothes: 0xb6ff7f,
  hair: 0xff9ecb,
}
const FALLBACK_COLOR = 0xcccccc

/**
 * Mount one mesh per branch under `root`.
 *
 * @param {THREE.Object3D} root      the avatar root the SplatMesh is placed in
 * @param {Array<{name: string, verts: Float32Array, mesh: {faces: Uint32Array}|null,
 *                neutralVertices: Float32Array}>} branches
 * @param {{scale: number, offset: readonly [number, number, number],
 *          rotation: THREE.Quaternion|null, wireframe: boolean}} placement
 * @returns {{update: () => void, dispose: () => void, count: number}|null}
 *   null when no branch carries a face list — nothing to draw, said once by the
 *   caller rather than mounting an empty group that looks like a broken render.
 */
export function createBranchMeshOverlay(root, branches, {
  scale = 1, offset = [0, 0, 0], rotation = null, wireframe = true,
} = {}) {
  const group = new THREE.Group()
  group.name = 'ogs-branch-mesh-overlay'
  const parts = []
  for (const br of branches ?? []) {
    const faces = br?.mesh?.faces
    const verts = br?.verts ?? br?.neutralVertices
    if (!faces?.length || !verts?.length) continue
    const geometry = new THREE.BufferGeometry()
    // A COPY, not a view onto the branch's live array: three keeps its own
    // upload bookkeeping on the attribute, and the branch reuses that buffer.
    const position = new THREE.BufferAttribute(Float32Array.from(verts), 3)
    position.setUsage(THREE.DynamicDrawUsage)
    geometry.setAttribute('position', position)
    geometry.setIndex(new THREE.BufferAttribute(Uint32Array.from(faces), 1))
    geometry.computeVertexNormals()
    const color = BRANCH_COLORS[br.name] ?? FALLBACK_COLOR
    const material = wireframe
      ? new THREE.MeshBasicMaterial({ color, wireframe: true })
      // Unlit on purpose: a live splat scene mounts an ambientLight only until an
      // IBL applies, so a lit material renders this black in exactly the scene it
      // is most needed in (the same trap the backdrop plane documents).
      : new THREE.MeshNormalMaterial({ flatShading: true })
    const mesh = new THREE.Mesh(geometry, material)
    mesh.name = `ogs-mesh-${br.name}`
    mesh.frustumCulled = false
    applyMeshPlacement(mesh, scale, offset, rotation)
    group.add(mesh)
    parts.push({ br, position, geometry, material })
  }
  if (!parts.length) return null
  root.add(group)

  return {
    count: parts.length,
    /** Re-read every branch's posed vertices. Call after a pose, not per frame:
     *  it copies V*3 floats and recomputes normals, which is the cost that keeps
     *  this behind a flag. */
    update() {
      for (const { br, position, geometry, material } of parts) {
        const v = br.verts
        if (!v || v.length !== position.array.length) continue
        position.array.set(v)
        position.needsUpdate = true
        // Only the shaded material reads normals; the wireframe does not, and
        // recomputing them for it would be the largest cost of a view that draws
        // lines. Bounds are skipped entirely — frustumCulled is off.
        if (material.isMeshNormalMaterial) geometry.computeVertexNormals()
      }
    },
    dispose() {
      root.remove(group)
      for (const { geometry, material } of parts) { geometry.dispose(); material.dispose() }
      parts.length = 0
    },
  }
}
